import { Fragment, memo, type ReactNode } from "react";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { styled } from "../../../../lib/styled";
import { SCORECARD, type ScorecardKey } from "@shared/scorecard";
import { fieldLabel } from "../../../../lib/scorecardFieldUtils";
import { SectionLabel } from "../../../primitives/data-display/SectionLabel";
import { Note } from "../../../primitives/data-display/Note";
import { TierChip } from "../../data-display/TierChip";
import { gaps } from "../../../primitives/spacing";
import type { OverrideInteraction } from "../../../../lib/corpusApi";

export type { OverrideInteraction };

/** Common base props shared by FieldReviewPanel and MultiVoterFieldPanel. */
export interface FieldPanelBase {
  field: ScorecardKey;
  override: OverrideInteraction;
  saving: boolean;
  onOverride: () => void;
  onMarkEmpty: () => void;
  /** Reject the current document and advance to the next item. */
  onReject?: () => void;
  /** Whether the inline help panel is expanded. */
  helpOpen: boolean;
  /** Toggle the inline help panel. */
  onHelpToggle: () => void;
}

interface FieldPanelHeaderProps {
  field: ScorecardKey;
  /** Secondary chip rendered after the Tier badge (e.g. confidence %, consensus %). */
  badge?: ReactNode;
  helpOpen: boolean;
  onHelpToggle: () => void;
}

interface FieldPanelActionsProps {
  /** Panel-specific primary action rendered before the shared buttons (e.g. Approve, Accept N). */
  primary?: ReactNode;
  saving: boolean;
  onOverride: () => void;
  onMarkEmpty: () => void;
  onReject?: () => void;
}

export interface HelpPanelProps {
  description: string | undefined;
  vocab: string[];
  labelDescriptions: Record<string, string> | undefined;
}

/** Root container for a field review panel — full height, scrollable, padded. */
export const PanelRoot = styled(Stack)({ height: "100%", padding: 16, overflowY: "auto" });

/** Chip row that wraps onto multiple lines. */
export const ChipRow = styled(Stack)({ flexWrap: "wrap" });

/** Note rendered in italic — for "nothing extracted" and similar empty-state messages. */
export const ItalicNote = styled(Note)({ fontStyle: "italic" });

/** Neutral-black fill for the panel's primary (contained) buttons. */
export const PanelContainedButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.text.primary,
  "&:hover": { backgroundColor: theme.palette.common.black },
}));

const FieldHeaderStack = styled(Stack)({ alignItems: "center" });

const AutoMarginHelpToggle = styled(IconButton)({ marginLeft: "auto" });

const HelpIcon = styled(InfoOutlinedIcon)<{ $open: boolean }>(({ theme, $open }) => ({
  color: $open ? theme.palette.primary.main : theme.palette.text.secondary,
}));

const HelpDl = styled("dl")(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(1.5),
  borderRadius: `${theme.shape.borderRadius}px`,
  backgroundColor: theme.palette.action.hover,
  fontSize: "0.8rem",
  lineHeight: 1.6,
  "& dt": {
    fontWeight: 700,
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(0.25),
    "&:first-of-type": { marginTop: 0 },
  },
  "& dd": { margin: 0, color: theme.palette.text.secondary },
}));

const ActionRow = styled(Stack)({ flexWrap: "wrap" });

/** Field label + Tier chip + optional secondary badge chip + help toggle icon. */
export function FieldPanelHeader({ field, badge, helpOpen, onHelpToggle }: FieldPanelHeaderProps) {
  const tier = SCORECARD[field]?.tier;
  return (
    <FieldHeaderStack direction="row" gap={gaps.tight}>
      <SectionLabel noGutter>{fieldLabel(field)}</SectionLabel>
      {tier && <TierChip tier={tier} />}
      {badge}
      <AutoMarginHelpToggle
        size="small"
        onClick={onHelpToggle}
        aria-label={helpOpen ? "Hide help" : "Show help (?)"}
      >
        <HelpIcon fontSize="small" $open={helpOpen} />
      </AutoMarginHelpToggle>
    </FieldHeaderStack>
  );
}

/**
 * Inline help panel showing the field description and per-label rubrics.
 * Memoized — props come from the static SCORECARD constant and only change when `field` changes.
 */
export const HelpPanel = memo(function HelpPanel({
  description,
  vocab,
  labelDescriptions,
}: HelpPanelProps) {
  return (
    <HelpDl>
      {description && <p style={{ margin: "0 0 10px" }}>{description}</p>}
      {labelDescriptions &&
        vocab.map((label) => {
          const desc = labelDescriptions[label];
          if (!desc) return null;
          return (
            <Fragment key={label}>
              <dt>{label}</dt>
              <dd>{desc}</dd>
            </Fragment>
          );
        })}
    </HelpDl>
  );
});

/** Button row: optional primary slot + shared Override + Mark empty + optional Reject buttons. */
export function FieldPanelActions({
  primary,
  saving,
  onOverride,
  onMarkEmpty,
  onReject,
}: FieldPanelActionsProps) {
  return (
    <ActionRow direction="row" gap={gaps.snug}>
      {primary}
      <Button
        variant="outlined"
        color="inherit"
        size="small"
        onClick={onOverride}
        disabled={saving}
      >
        O Override
      </Button>
      <Button
        variant="outlined"
        color="inherit"
        size="small"
        onClick={onMarkEmpty}
        disabled={saving}
      >
        E Mark empty
      </Button>
      {onReject && (
        <Button variant="outlined" color="error" size="small" onClick={onReject} disabled={saving}>
          Reject
        </Button>
      )}
    </ActionRow>
  );
}
